
import React, { useCallback, useState, useMemo, useEffect } from 'react';
// Fix: Use named imports for ReactFlow hooks from reactflow.
import {
  useNodesState,
  useEdgesState,
  useReactFlow,
  Edge,
  Node
} from 'reactflow';
import { getLayoutedElements } from '../utils/autoLayout';
import { useProcessManagement } from './useProcessManagement';
import { useFileOperations } from './useFileOperations';
import { useNodeOperations } from './useNodeOperations';
import { useEdgeOperations } from './useEdgeOperations';
import { useAutoSave, AutoSavePayload } from './useAutoSave';
import { ProcessDefinition, WorkflowProject, WorkflowNodeData, Swimlane } from '../types';
import { DEFAULT_LANE_WIDTH, DEFAULT_LANE_HEIGHT, DEFAULT_LANE_COLOR } from '../components/molecules/Swimlanes';
import { getAvailableVariables } from '../utils/variableExtraction';
import { generateWorkflowProject } from '../utils/pssHelper';

interface UseWorkflowEditorProps {
    initialData?: ProcessDefinition[];
    onAutoSave?: (payload: AutoSavePayload) => void;
}

export const useWorkflowEditor = ({ initialData, onAutoSave }: UseWorkflowEditorProps = {}) => {
  // Determine start state from props or default (Empty Shell if none provided)
  const startingProcess = (initialData && initialData.length > 0) 
      ? initialData[0] 
      : { id: 'proc_1', name: 'New Process', nodes: [], edges: [] } as ProcessDefinition;

  // Deep Clone initial state to avoid reference issues
  const initialProcessClone = startingProcess ? JSON.parse(JSON.stringify(startingProcess)) : { nodes: [], edges: [] };
  
  const { fitView, setCenter } = useReactFlow();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // React Flow State Initialization
  const initNodes = initialProcessClone.nodes || (initialProcessClone as any).Nodes || [];
  const initEdges = initialProcessClone.edges || (initialProcessClone as any).Connections || [];

  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);

  // Virtual swimlanes: independent, movable, resizable rectangles. Each lane
  // corresponds to an actorType (lane.name). Source of truth, in order:
  //   1. Explicit process.swimlanes (restored from Editor.Swimlanes extension)
  //   2. Derived from existing node actorTypes as a fallback (legacy projects)
  const [swimlanes, setSwimlanes] = useState<Swimlane[]>(() => {
    const restored = (initialProcessClone as any).swimlanes;
    if (Array.isArray(restored) && restored.length > 0) return restored as Swimlane[];

    const fromNodes = new Set<string>();
    (initNodes as any[]).forEach((n: any) => {
      if (n?.data?.actorType) fromNodes.add(n.data.actorType);
    });
    return Array.from(fromNodes).sort().map((name, i) => ({
      id: `lane_${Date.now()}_${i}`,
      name,
      x: 0,
      y: i * (DEFAULT_LANE_HEIGHT + 20),
      width: DEFAULT_LANE_WIDTH,
      height: DEFAULT_LANE_HEIGHT,
      color: DEFAULT_LANE_COLOR,
    }));
  });

  const [selectedSwimlaneId, setSelectedSwimlaneId] = useState<string | null>(null);

  const addSwimlane = useCallback((position?: { x: number; y: number }) => {
    setSwimlanes(prev => {
      const existing = new Set(prev.map(l => l.name));
      let i = prev.length + 1;
      let name = `Actor ${i}`;
      while (existing.has(name)) { i++; name = `Actor ${i}`; }
      const pos = position ?? {
        x: 40,
        y: prev.length > 0
          ? Math.max(...prev.map(l => l.y + l.height)) + 20
          : 40,
      };
      const lane: Swimlane = {
        id: `lane_${Date.now()}`,
        name,
        x: pos.x,
        y: pos.y,
        width: DEFAULT_LANE_WIDTH,
        height: DEFAULT_LANE_HEIGHT,
        color: DEFAULT_LANE_COLOR,
      };
      return [...prev, lane];
    });
  }, []);

  const renameSwimlane = useCallback((id: string, newName: string) => {
    setSwimlanes(prev => {
      const lane = prev.find(l => l.id === id);
      if (!lane) return prev;
      const oldName = lane.name;
      if (oldName === newName) return prev;
      // Propagate rename to nodes.
      setNodes(nds => nds.map(n =>
        n.data.actorType === oldName
          ? { ...n, data: { ...n.data, actorType: newName } }
          : n
      ));
      return prev.map(l => l.id === id ? { ...l, name: newName } : l);
    });
  }, [setNodes]);

  // Delta-based move: callers pass the *incremental* (dx, dy) applied since
  // the previous tick. This lets us update lane and member nodes by exactly
  // the same amount WITHOUT nesting setNodes inside a setSwimlanes updater
  // — which in React Strict Mode would fire the nested dispatch twice and
  // double the node translation.
  const moveSwimlaneBy = useCallback((id: string, dx: number, dy: number) => {
    if (dx === 0 && dy === 0) return;
    const lane = swimlanes.find(l => l.id === id);
    if (!lane) return;
    const actorName = lane.name;
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, x: l.x + dx, y: l.y + dy } : l));
    setNodes(nds => nds.map(n => {
      if (n.data.actorType !== actorName) return n;
      if ((n as any).parentNode) return n;
      return { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } };
    }));
  }, [swimlanes, setNodes]);

  // Absolute-position setter (kept for programmatic / auto-layout callers).
  const moveSwimlane = useCallback((id: string, x: number, y: number) => {
    const lane = swimlanes.find(l => l.id === id);
    if (!lane) return;
    moveSwimlaneBy(id, x - lane.x, y - lane.y);
  }, [swimlanes, moveSwimlaneBy]);

  const resizeSwimlane = useCallback((id: string, width: number, height: number) => {
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, width, height } : l));
  }, []);

  const setSwimlaneColor = useCallback((id: string, color: string) => {
    setSwimlanes(prev => prev.map(l => l.id === id ? { ...l, color } : l));
  }, []);

  const removeSwimlane = useCallback((id: string) => {
    setSwimlanes(prev => {
      const removed = prev.find(l => l.id === id);
      if (removed) {
        setNodes(nds => nds.map(n => {
          if (n.data.actorType !== removed.name) return n;
          const { actorType, ...rest } = n.data;
          return { ...n, data: rest as WorkflowNodeData };
        }));
      }
      return prev.filter(l => l.id !== id);
    });
    setSelectedSwimlaneId(curr => curr === id ? null : curr);
  }, [setNodes]);

  const selectedSwimlane = useMemo(
    () => swimlanes.find(l => l.id === selectedSwimlaneId) || null,
    [swimlanes, selectedSwimlaneId]
  );

  // Delete / Backspace removes the currently selected swimlane — but only
  // when the keystroke isn't consumed by a form field (input / textarea /
  // contenteditable). ReactFlow already handles Delete for nodes/edges; if
  // a swimlane is selected we take priority since lanes aren't RF nodes.
  useEffect(() => {
    if (!selectedSwimlaneId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        (target && (target as HTMLElement).isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      removeSwimlane(selectedSwimlaneId);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedSwimlaneId, removeSwimlane]);

  // Sub-hooks
  const {
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      handleSelectProcess,
      handleCreateProcess,
      handleCreateSubProcess,
      handleDeleteProcess,
      handleUpdateProcess
  } = useProcessManagement(nodes, setNodes, edges, setEdges, initialData);

  const {
      handleSaveProject,
      handleLoadProject,
      projectName
  } = useFileOperations(
      nodes,
      edges,
      processes,
      setProcesses,
      activeProcessId,
      setActiveProcessId,
      setNodes,
      setEdges,
      setIsLoading,
      swimlanes,
      setSwimlanes,
  );

  const {
      selectedNode,
      setSelectedNode,
      isModalOpen,
      setIsModalOpen,
      activeTabId,
      setActiveTabId,
      openModalAtTab,
      onDragOver,
      onDrop,
      onNodeClick: originalOnNodeClick,
      onPaneClick: originalOnPaneClick,
      onNodeDoubleClick,
      onNodeContextMenu,
      onNodeDragStop,
      updateNodeData,
      renameNode
  } = useNodeOperations(nodes, setNodes, setEdges, swimlanes);

  const {
      onConnect,
      onConnectStart,
      onConnectEnd
  } = useEdgeOperations(nodes, setNodes, setEdges);

  // Wrapped handlers to manage mutual exclusion between Node, Edge and Swimlane
  // selection. The PropertiesPanel renders whichever of the three is set, in
  // priority swimlane > edge > node, so switching selections must clear the
  // others — otherwise the panel stays stuck on the previous selection.
  const onNodeClick = useCallback((e: React.MouseEvent, node: Node) => {
      setSelectedEdgeId(null);
      setSelectedSwimlaneId(null);
      originalOnNodeClick(e, node);
  }, [originalOnNodeClick]);

  const onEdgeClick = useCallback((_e: React.MouseEvent, edge: Edge) => {
      setSelectedNode(null);
      setSelectedSwimlaneId(null);
      setSelectedEdgeId(edge.id);
  }, [setSelectedNode]);

  const onPaneClick = useCallback(() => {
      setSelectedEdgeId(null);
      setSelectedSwimlaneId(null);
      originalOnPaneClick();
  }, [originalOnPaneClick]);

  // Select a swimlane and clear other selections.
  const selectSwimlane = useCallback((id: string | null) => {
      setSelectedSwimlaneId(id);
      if (id) {
          setSelectedEdgeId(null);
          setSelectedNode(null);
      }
  }, [setSelectedNode]);

  // React Flow's authoritative selection callback. Fires whenever the
  // internal node/edge selection changes — including cases where
  // onNodeClick is skipped (drag-ish clicks, keyboard selection, etc.).
  // Always call the setters — stale closures on selectedNode/selectedEdgeId
  // caused the sync to short-circuit previously. Primitive-ID setters no-op
  // on unchanged values, so there's no extra render cost.
  const onSelectionChange = useCallback((params: { nodes: Node[]; edges: Edge[] }) => {
      const selNodeId = params.nodes[0]?.id ?? null;
      const selEdgeId = params.edges[0]?.id ?? null;
      const node = selNodeId ? nodes.find(n => n.id === selNodeId) ?? null : null;
      setSelectedNode(node);
      setSelectedEdgeId(selEdgeId);
      // Clear swimlane selection whenever ReactFlow reports a node or edge
      // selection — keeps the three selection sources mutually exclusive and
      // prevents the PropertiesPanel from sticking on the previous selection.
      if (selNodeId || selEdgeId) setSelectedSwimlaneId(null);
  }, [nodes, setSelectedNode]);

  const selectedEdge = useMemo(() => {
      if (!selectedEdgeId) return null;
      return edges.find(e => e.id === selectedEdgeId) || null;
  }, [edges, selectedEdgeId]);

  const updateEdgeData = useCallback((id: string, newData: Partial<Edge>) => {
      setEdges(eds => eds.map(e => {
          if (e.id === id) {
              return { ...e, ...newData };
          }
          return e;
      }));
  }, [setEdges]);

  // Auto-Save Hook with callback support
  const autoSaveState = useAutoSave(processes, activeProcessId, nodes, edges, onAutoSave);

  // --- Variable Context Calculation ---
  const variableContext = useMemo(() => {
      if (!selectedNode) return [];
      return getAvailableVariables(nodes, edges, selectedNode.id);
  }, [selectedNode, nodes, edges, isModalOpen]);

  // --- Auto Layout ---
  const handleAutoLayout = useCallback(() => {
    try {
      const result = getLayoutedElements(nodes, edges, 'LR', swimlanes);
      setNodes([...result.nodes]);
      setSwimlanes([...result.swimlanes]);
      setTimeout(() => fitView({ duration: 800 }), 10);
    } catch (err) {
      // Surface layout errors instead of silently no-op'ing — otherwise a
      // broken layout looks indistinguishable from 'nothing happened'.
      console.error('Auto layout failed:', err);
    }
  }, [nodes, edges, swimlanes, setNodes, fitView]);

  // Viewport helper
  const focusNode = useCallback((nodeId: string) => {
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
          setCenter(node.position.x + 90, node.position.y + 32, { zoom: 1.2, duration: 800 });
      }
  }, [nodes, setCenter]);

  // Helper to get current full project state in PSS format (for external saves)
  const getProjectData = useCallback((): WorkflowProject => {
      const currentSnapshot = processes.map(p =>
          p.id === activeProcessId ? { ...p, nodes, edges, swimlanes } : p
      );
      return generateWorkflowProject(currentSnapshot, projectName);
  }, [processes, activeProcessId, nodes, edges, swimlanes, projectName]);

  return {
    nodes, setNodes, onNodesChange,
    edges, setEdges, onEdgesChange,
    processes, activeProcessId,
    selectedNode, setSelectedNode,
    selectedEdge,
    variableContext, // Export context
    isModalOpen, setIsModalOpen,
    activeTabId,
    setActiveTabId,
    openModalAtTab,
    isDeployModalOpen, setIsDeployModalOpen,
    handleSelectProcess, handleCreateProcess, handleCreateSubProcess, handleDeleteProcess, handleUpdateProcess,
    handleSaveProject, handleLoadProject,
    handleAutoLayout,
    onConnect, onConnectStart, onConnectEnd, onDrop, onDragOver,
    onNodeClick, onEdgeClick, onPaneClick, onNodeDoubleClick, onNodeContextMenu, onNodeDragStop, onSelectionChange,
    updateNodeData, updateEdgeData, renameNode, fitView, focusNode,
    swimlanes, addSwimlane, renameSwimlane, removeSwimlane,
    moveSwimlane, moveSwimlaneBy, resizeSwimlane, setSwimlaneColor,
    selectedSwimlane, selectedSwimlaneId, selectSwimlane,
    setIsLoading, isLoading, autoSaveState,
    getProjectData
  };
};